import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const buildJianyingImageVariants = (image = {}) => [
  image.image_url && { key: 'original', label: '原图', url: image.image_url },
  image.fix_image_url && { key: 'fixed', label: '优化图', url: image.fix_image_url },
  image.crop_image_url && { key: 'crop', label: '裁剪图', url: image.crop_image_url },
].filter(Boolean);

const buildJianyingMaterialImageVariants = (image = {}) => [
  image.image_url && {
    key: 'original', source: 'original', label: '原图', url: image.image_url,
  },
  image.crop_image_url
    ? {
      key: 'processed', source: 'processed', label: '裁剪图', url: image.crop_image_url,
    }
    : image.fix_image_url
      ? {
        key: 'processed', source: 'processed', label: '优化图', url: image.fix_image_url,
      }
      : null,
].filter(Boolean);

const JianyingImageCard = ({
  assignmentLabel = '',
  busyLabel = '',
  copyDisabled = false,
  disabled = false,
  entry,
  index,
  isSelected = false,
  mode = 'material',
  onAutoCrop = () => {},
  onCopy = () => {},
  onManualProcessingCrop = () => {},
  onPreview = () => {},
  onRemove = () => {},
  onRepair = () => {},
  onSelect = () => {},
  onSourceChange = () => {},
  onToggleSelected = () => {},
  readOnly = false,
  showSequenceControls = false,
  sortable = false,
  suppressDropAnimation = false,
  variants: providedVariants,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: entry.sortId || entry.id, disabled: disabled || !sortable });
  const image = entry?.image;
  const item = entry?.item;
  const unavailable = entry?.state === 'unavailable';
  const displayIndex = Number.isInteger(index)
    ? String(index + 1).padStart(2, '0')
    : '—';

  if (mode === 'processing') {
    const variants = buildJianyingImageVariants(image);
    const locked = disabled || Boolean(busyLabel);
    return (
      <article className={`jianying-processing-card ${isSelected ? 'is-selected' : ''}`}>
        <header className="jianying-processing-card__header">
          <label>
            <input
              type="checkbox"
              checked={isSelected}
              disabled={locked}
              onChange={() => onToggleSelected(image)}
            />
            <strong>{image?.name || `图片 ${displayIndex}`}</strong>
          </label>
          {busyLabel ? <span>{busyLabel}中…</span> : null}
        </header>
        <div className={`jianying-processing-filmstrip has-${variants.length}`}>
          {variants.map((variant) => (
            <figure key={variant.key}>
              <button
                type="button"
                disabled={!variant.url}
                onClick={() => onPreview({
                  alt: `${image?.name || '图片'} ${variant.label}`,
                  src: variant.url,
                  title: `${image?.name || '图片'} · ${variant.label}`,
                })}
              >
                <img loading="lazy" src={variant.url} alt={`${image?.name || ''} ${variant.label}`} />
              </button>
              <figcaption>{variant.label}</figcaption>
            </figure>
          ))}
        </div>
        <footer className="jianying-processing-card__actions">
          <button type="button" disabled={locked} onClick={() => onRepair(image)}>一键修复</button>
          <button type="button" disabled={locked} onClick={() => onAutoCrop(image)}>自动裁剪</button>
          <button type="button" disabled={locked} onClick={() => onManualProcessingCrop(image)}>手动裁剪</button>
        </footer>
      </article>
    );
  }

  const variants = providedVariants || buildJianyingMaterialImageVariants(image);
  const hasProcessed = variants.some((variant) => variant.source === 'processed');
  const selectedSource = item?.source === 'original'
    ? 'original'
    : hasProcessed ? 'processed' : 'original';
  const selectSource = (source) => {
    if (isSelected) onSourceChange(source);
    else onSelect(image, source);
  };
  return (
    <article
      ref={setNodeRef}
      style={{
        transform: CSS.Translate.toString(transform),
        transition: suppressDropAnimation ? 'none' : transition,
      }}
      className={`jianying-group-image-card is-${entry?.state || 'unselected'} ${
        variants.length > 1 ? 'has-comparison' : ''
      } ${disabled || unavailable ? 'is-disabled' : ''} ${isDragging ? 'is-dragging' : ''}`}
    >
      <div className="jianying-group-image-card__header">
        <div className="jianying-group-image-card__title">
          <span className={assignmentLabel ? 'is-assignment' : ''}>
            {assignmentLabel || displayIndex}
          </span>
          <strong>{image?.name || item?.image_id || ''}</strong>
        </div>
        {isSelected && !readOnly ? (
          <button
            type="button"
            className="jianying-group-image-card__remove"
            disabled={disabled}
            onClick={onRemove}
            aria-label={`取消选中第 ${Number(index) + 1} 张图片`}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
            <span className="jianying-group-image-card__remove-tooltip" role="tooltip">取消选中</span>
          </button>
        ) : null}
      </div>

      <div className={`jianying-group-image-card__compare ${variants.length > 1 ? '' : 'is-single'}`}>
        {variants.map((variant) => (
          <div key={variant.key} className="jianying-group-image-card__choice-option">
            <button
              type="button"
              className={`jianying-group-image-card__choice ${
                isSelected && selectedSource === variant.source ? 'is-active' : ''
              }`}
              disabled={readOnly || disabled || unavailable}
              onClick={() => selectSource(variant.source)}
              aria-pressed={isSelected && selectedSource === variant.source}
            >
              <span className="jianying-group-image-card__choice-preview">
                <img loading="lazy" src={variant.url} alt={`${image?.name || ''} ${variant.label}`} />
              </span>
              <span className="jianying-group-image-card__choice-label">
                <strong>{variant.label}</strong>
              </span>
            </button>
            <button
              type="button"
              className="jianying-group-image-card__choice-preview-action"
              onClick={() => onPreview({
                alt: `${image?.name || ''} ${variant.label}`,
                src: variant.url,
                title: `${image?.name || '图片'} · ${variant.label}`,
              })}
              aria-label={`放大预览 ${image?.name || '图片'} ${variant.label}`}
              title="放大预览"
            >
              <i className="bi bi-arrows-fullscreen" aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>

      {isSelected && showSequenceControls ? (
        <div className="jianying-group-image-card__footer">
          <button
            type="button"
            className="jianying-group-image-card__drag"
            disabled={disabled}
            aria-label={`拖动第 ${Number(index) + 1} 张图片调整顺序`}
            title="拖动排序"
            {...attributes}
            {...listeners}
          >
            <i className="bi bi-grip-vertical" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="jianying-group-image-card__copy"
            disabled={disabled || copyDisabled || !image}
            onClick={onCopy}
          >
            <i className="bi bi-copy" aria-hidden="true" />
            <span>复制</span>
          </button>
        </div>
      ) : null}
    </article>
  );
};

export default JianyingImageCard;
